import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

import { StackedDialog } from '../utilities/ModalStackContext';
import { StateManager } from '../utilities/stateManager';

const speechSupported =
  typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

// Shared "notes before clocking out" dialog. Captures a work note (voice or
// typed) and hands the trimmed text to onConfirm. Notes are required — the
// confirm button stays disabled until there's text. Used by MechanicClockPage
// and the Mechanic Orders page.
//
// allowSkip: when true (admin/manager clocking a mechanic out from the service
// order), a secondary "Clock out without note" action is shown that calls
// onSkip. Self-serve mechanic flows leave allowSkip falsy so a note stays
// required.
export default function ClockOutNotesDialog({ open, onClose, onConfirm, onSkip, allowSkip = false, loading = false }) {
  const [transcript, setTranscript] = useState('');
  const [recording, setRecording] = useState(false);
  const finalRef = useRef('');
  const recognitionRef = useRef(null);

  const startRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      StateManager.setAlertAndOpen('Speech recognition not supported here — type notes instead', 'warning');
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalRef.current += t + ' ';
        else interim += t;
      }
      setTranscript(finalRef.current + interim);
    };
    rec.onerror = (e) => {
      console.error('SpeechRecognition error', e);
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        StateManager.setAlertAndOpen('Mic error: ' + e.error, 'error');
      }
      setRecording(false);
    };
    rec.onend = () => setRecording(false);
    try {
      rec.start();
      recognitionRef.current = rec;
      setRecording(true);
    } catch (e) {
      StateManager.setAlertAndOpen('Could not start mic: ' + (e?.message || e), 'error');
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current && recognitionRef.current.stop(); } catch (_) {}
    recognitionRef.current = null;
    setRecording(false);
  };

  const reset = () => {
    stopRecording();
    setTranscript('');
    finalRef.current = '';
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose && onClose();
  };

  const handleConfirm = () => {
    const text = transcript.trim();
    if (!text) return;
    reset();
    onConfirm && onConfirm(text);
  };

  const handleSkip = () => {
    if (loading) return;
    reset();
    onSkip && onSkip();
  };

  return (
    <StackedDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Notes before clocking out</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe what you worked on — we'll clean it up and add it to the service order.
          <br />
          <strong>Notes are required to clock out.</strong> Tap Back to keep working.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {!recording ? (
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<MicIcon />}
              onClick={startRecording}
              disabled={!speechSupported}
              sx={{ py: 2, px: 4, fontSize: 18 }}
            >
              {speechSupported ? 'Tap to record' : 'Mic not supported'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              color="error"
              startIcon={<StopIcon />}
              onClick={stopRecording}
              sx={{ py: 2, px: 4, fontSize: 18 }}
            >
              Stop
            </Button>
          )}
        </Box>
        <TextField
          label="Notes"
          value={transcript}
          onChange={(e) => {
            finalRef.current = e.target.value;
            setTranscript(e.target.value);
          }}
          placeholder="e.g. Replaced front brake pads, checked rotors, topped off coolant…"
          multiline
          rows={5}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Back</Button>
        {allowSkip && (
          <Button onClick={handleSkip} disabled={loading} color="inherit">
            Clock out without note
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || !transcript.trim()}
        >
          Save & Clock Out
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
